import React, { FC, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import duration from 'dayjs/plugin/duration';
import DayjsUtils from '@date-io/dayjs';

import { Box, Button, FormControl, FormHelperText, FormLabel, makeStyles, Modal, TextField, Typography } from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';
import PlayCircle from '@material-ui/icons/PlayCircleFilled';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import Fers from './Fers';
import { createFer, deleteFer, sendFerToHarvest } from './apiMutations';
import { useAlertContext } from '../../context/alertContext';
import { convertFerToFerInput } from '../../utils/inputTransform';
import {
	CreateFerVariables,
	CreateOrUpdateFerResult,
	DeleteFerResult,
	DeleteFerVariables,
	Fer,
	LoadingOptions,
	SendFerToHarvestResult,
	SendFerToHarvestVariables
} from '../../utils/types';

dayjs.extend(duration);
dayjs.extend(advancedFormat);

const MANUAL_HARVEST_WINDOW_DAYS = 14;

const useStyles = makeStyles((theme) => ({
	centralModal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	modalBox: {
		background: '#FFFFFF',
		overflowY: 'hidden',
		margin: '10vh',
		borderRadius: 4,
		[theme.breakpoints.down('sm')]: {
			minWidth: '40vw'
		},
		[theme.breakpoints.up('sm')]: {
			minWidth: '40vw',
			maxWidth: '40vw'
		}
	},
	modalToolbar: {
		backgroundColor: '#1976d2',
		color: 'white',
		paddingLeft: '1vw',
		paddingTop: '1vh',
		paddingBottom: '1vh',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	titleBox: {
		display: 'flex',
		alignItems: 'center'
	},
	modalContent: {
		paddingTop: '1.5vh',
		paddingLeft: '1vw',
		paddingRight: '1vw',
		paddingBottom: '1.5vh',
		display: 'flex',
		flexDirection: 'column'
	},
	modalFooter: {
		backgroundColor: '#1976d2',
		color: 'white',
		padding: '0.5vw',
		display: 'block',
		textAlign: 'right'
	}
}));

const sendToHarvestTitle = 'Send FER to Harvest';
const sendToHarvestIcon = <PlayCircle />;

export const FersEditor: FC<FersEditorProps> = (props) => {
	const { fers, loadingOptions, gameId, gameStartTime } = props;

	const { setAlert } = useAlertContext() || [];
	const [createFerMethod] = useMutation<CreateOrUpdateFerResult, CreateFerVariables>(createFer);
	const [deleteFerMethod] = useMutation<DeleteFerResult, DeleteFerVariables>(deleteFer);
	const [sendToHarvestMethod] = useMutation<SendFerToHarvestResult, SendFerToHarvestVariables>(sendFerToHarvest);

	const [sendToHarvestModalOpen, setSendToHarvestModalOpen] = useState(false);
	const [harvestFer, setHarvestFer] = useState<HarvestFer | null>(null);
	const [ferEndTime, setFerEndTime] = useState<string | null>(null);

	const classes = useStyles();

	const toggleSendToHarvestModal = (): void => {
		setSendToHarvestModalOpen(!sendToHarvestModalOpen);
	};

	const handleSendToHarvest = (fer: HarvestFer): void => {
		setHarvestFer(fer);
		setFerEndTime(dayjs().toISOString());
		setSendToHarvestModalOpen(true);
	};

	const sendToHarvestConfirm = async (): Promise<void> => {
		if (!harvestFer || !ferEndTime) return;
		let requestError = false;
		await sendToHarvestMethod({
			variables: { ferId: harvestFer.ferId, endTime: ferEndTime }
		}).catch(() => {
			setAlert('There was a problem with sending the FER to harvest. Review the timings and try again.');
			requestError = true;
		});

		if (!requestError) {
			setAlert('FER sent to harvest successfully');
			setSendToHarvestModalOpen(false);
			setHarvestFer(null);
			setFerEndTime(null);
			loadingOptions.handleReload();
		}
	};

	const sendToHarvestCancel = (): void => {
		setSendToHarvestModalOpen(false);
		setHarvestFer(null);
		setFerEndTime(null);
	};

	const handleFerAdd = (newData: Fer): Promise<unknown> => {
		const fer = convertFerToFerInput(gameId, newData);

		const createdFer = createFerMethod({
			variables: { fer }
		}).catch((error) => {
			setAlert(error.toString());
		});

		return createdFer.then(() => loadingOptions.handleReload());
	};

	const handleFerDelete = (oldData: Fer): Promise<unknown> => {
		const { ferId } = oldData;

		const deletedFer = deleteFerMethod({
			variables: { gameId, ferId }
		}).catch((error) => {
			setAlert(error.toString());
		});

		return deletedFer.then(() => loadingOptions.handleReload());
	};

	const renderSendToHarvestModal = (): JSX.Element => {
		return (
			<Modal open={sendToHarvestModalOpen} className={classes.centralModal} onClose={(): void => toggleSendToHarvestModal()}>
				<Box className={classes.modalBox}>
					<Box className={classes.modalToolbar}>
						<Box className={classes.titleBox}>
							{sendToHarvestIcon}
							<Typography variant='h5' style={{ paddingLeft: '0.75vw' }}>
								{sendToHarvestTitle}
							</Typography>
						</Box>
						<Button onClick={(): void => toggleSendToHarvestModal()}>
							<CloseIcon style={{ color: 'white', marginRight: '0.5vw' }} />
						</Button>
					</Box>
					<Box className={classes.modalContent}>
						<p style={{ marginTop: 0 }}>
							Sending an FER harvest request will start the process of recording FER content and is dependent upon the selected Channel having been
							active during the target period. You <strong>cannot</strong> send a manual FER harvest request unless there was an Airing on the same
							Channel during Game time. However, if there was an Airing running throughout the Game you can request a harvest{' '}
							<strong>even if the FER placeholder was not configured</strong> at the time.
						</p>
						<p style={{ marginTop: 0 }}>
							<strong>Note:</strong> End Time must follow Start Time and FERs in excess of 4hrs will be rejected. Airing content only remains
							available for {MANUAL_HARVEST_WINDOW_DAYS} days and it will not be possible to manually harvest indefinitely.
						</p>
						<FormControl component='fieldset' style={{ marginTop: '1vh', marginBottom: '1vh' }}>
							<FormLabel component='legend'>Start Time</FormLabel>
							<TextField
								id='ferStartTime'
								variant='standard'
								disabled
								value={harvestFer?.startTime ? dayjs(harvestFer.startTime).format('MMMM Do hh:mm a') : ''}
								style={{ width: '250px' }}
							/>
						</FormControl>
						<FormControl component='fieldset' style={{ marginTop: '1vh', marginBottom: '1vh' }}>
							<FormLabel component='legend'>Channel Id</FormLabel>
							<TextField id='ferChannelId' variant='standard' disabled value={harvestFer?.channelId || ''} style={{ width: '300px' }} />
						</FormControl>
						<FormControl component='fieldset' style={{ marginTop: '1vh', marginBottom: '0' }}>
							<FormLabel component='legend'>End Time</FormLabel>
							<MuiPickersUtilsProvider utils={DayjsUtils}>
								<DateTimePicker
									id='ferEndTime'
									name='ferEndTime'
									value={ferEndTime}
									onChange={(date: dayjs.Dayjs | null): void => setFerEndTime(date?.toISOString() || '')}
									InputProps={{
										style: { fontSize: '0.8rem', width: '250px' }
									}}
								/>
							</MuiPickersUtilsProvider>
							<FormHelperText>
								Please make an accurate <strong>End Time</strong> selection before sending to harvest.
							</FormHelperText>
						</FormControl>
					</Box>
					<Box className={classes.modalFooter}>
						<Box>
							<Button onClick={(): void => sendToHarvestCancel()}>Cancel</Button>
							<Button variant='contained' color='secondary' style={{ marginLeft: '0.5vw' }} onClick={(): Promise<void> => sendToHarvestConfirm()}>
								Send to Harvest
							</Button>
						</Box>
					</Box>
				</Box>
			</Modal>
		);
	};

	if (loadingOptions.loading) return <Typography>Loading...</Typography>;
	if (loadingOptions.error) return <Typography>Error loading data from the server</Typography>;
	return (
		<>
			<Fers
				fers={fers}
				onRowAdd={handleFerAdd}
				onRowDelete={handleFerDelete}
				loadingOptions={loadingOptions}
				gameStartTime={gameStartTime}
				sendToHarvest={handleSendToHarvest}
				harvestDays={MANUAL_HARVEST_WINDOW_DAYS}
			/>
			{renderSendToHarvestModal()}
		</>
	);
};

type FersEditorProps = {
	fers: Fer[];
	gameId: string;
	loadingOptions: LoadingOptions;
	gameStartTime: string | null;
};

export interface HarvestFer {
	ferId: string;
	startTime: string;
	channelId: string;
}

export default FersEditor;
